/**
 * analytics.js — Centralized Firebase Analytics event helpers.
 *
 * All tracking calls go through this file so event names stay consistent
 * and we have a single place to add/remove events.
 *
 * Usage:
 *   import { trackEvent, trackPageView } from '../services/analytics';
 *   trackEvent('post_scheduled', { platform: 'youtube' });
 */
import { logEvent } from 'firebase/analytics';
import { getAnalyticsInstance } from '../firebase';

// Safe wrapper — silently no-ops if analytics isn't initialized (SSR, bots, no measurementId)
function track(eventName, params = {}) {
    const analytics = getAnalyticsInstance();
    if (!analytics) return;
    try {
        logEvent(analytics, eventName, params);
    } catch (e) {
        // Never let analytics crash the app
    }
}

// ─── Page Views ──────────────────────────────────────────────────────────────
export function trackPageView(pagePath, pageTitle) {
    track('page_view', { page_path: pagePath, page_title: pageTitle });
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export function trackSignIn(method = 'google') {
    track('login', { method });
}

export function trackSignOut() {
    track('sign_out');
}

// ─── Onboarding ───────────────────────────────────────────────────────────────
export function trackPlatformConnected(platform) {
    track('platform_connected', { platform });
}

export function trackPlatformDisconnected(platform) {
    track('platform_disconnected', { platform });
}

// ─── Posting ──────────────────────────────────────────────────────────────────
export function trackPostScheduled(platforms, isScheduled = false) {
    track('post_scheduled', {
        platforms: Array.isArray(platforms) ? platforms.join(',') : platforms,
        scheduled: isScheduled,
        platform_count: Array.isArray(platforms) ? platforms.length : 1,
    });
}

export function trackPostPublished(platforms) {
    track('post_published', {
        platforms: Array.isArray(platforms) ? platforms.join(',') : platforms,
        platform_count: Array.isArray(platforms) ? platforms.length : 1,
    });
}

export function trackMediaUploaded(mediaType, platform) {
    track('media_uploaded', { media_type: mediaType, platform });
}

// ─── AI Features ──────────────────────────────────────────────────────────────
export function trackSmartCompose(platformCount, tone) {
    track('smart_compose_used', { platform_count: platformCount, tone });
}

export function trackThumbnailGenerated(platform) {
    track('thumbnail_generated', { platform });
}

export function trackThumbnailApplied(platform) {
    track('thumbnail_applied', { platform });
}

export function trackContentOptimized(platform) {
    track('content_optimized', { platform });
}

export function trackGapAnalysis() {
    track('gap_analysis_run');
}

export function trackIdeasGenerated(platform, count) {
    track('ideas_generated', { platform, count });
}

// ─── Calendar ─────────────────────────────────────────────────────────────────
export function trackCalendarSlotClicked() {
    track('calendar_slot_clicked');
}

export function trackScheduleDrawerOpened(source) {
    // source: 'slot' | 'sidebar_button'
    track('schedule_drawer_opened', { source });
}

// ─── Engagement ───────────────────────────────────────────────────────────────
export function trackAutoReplyEnabled(platform) {
    track('auto_reply_enabled', { platform });
}

export function trackAutoReplyDisabled(platform) {
    track('auto_reply_disabled', { platform });
}

// ─── Billing & Upgrades ───────────────────────────────────────────────────────
export function trackUpgradeClicked(source, currentPlan) {
    track('upgrade_clicked', { source, current_plan: currentPlan });
}

export function trackTopupPurchased(creditAmount) {
    track('topup_purchased', { credit_amount: creditAmount });
}

export function trackPlanSelected(planName, interval) {
    track('plan_selected', { plan_name: planName, interval });
}

// ─── Navigation ───────────────────────────────────────────────────────────────
export function trackNavClick(destination) {
    track('nav_clicked', { destination });
}

// ─── Landing Page ─────────────────────────────────────────────────────────────
export function trackCTAClicked(ctaLabel, location) {
    track('cta_clicked', { cta_label: ctaLabel, location });
}

export function trackNewsletterSignup(topic) {
    track('newsletter_signup', { topic });
}

// ─── Quota ────────────────────────────────────────────────────────────────────
export function trackQuotaHit(feature) {
    track('quota_limit_hit', { feature });
}

// Generic escape hatch for one-off events
export { track as trackEvent };
